import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export default function AttendanceTab({ levelCode }) {
  const [students, setStudents] = useState([]);
  const [attendanceData, setAttendanceData] = useState({});
  const [attendanceDates, setAttendanceDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [viewMode, setViewMode] = useState('date'); // 'date' or 'student'
  const [expandedDates, setExpandedDates] = useState(new Set());
  const [expandedStudents, setExpandedStudents] = useState(new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerData, setDrawerData] = useState(null);
  const [drawerType, setDrawerType] = useState(null); // 'date' or 'student'

  // Helper function to get local date string
  function getLocalDateString(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  useEffect(() => {
    fetchStudents();
    fetchAllAttendanceData();
  }, [levelCode]);

  const fetchStudents = async () => {
    try {
      const { data, error } = await supabase
        .from('student_enrollment')
        .select(`
          student:users (
            id,
            name,
            code
          )
        `)
        .eq('level_code', levelCode);

      if (error) throw error;
      
      // Sort students by name after fetching
      const students = data.map(item => item.student).sort((a, b) => 
        a.name.localeCompare(b.name)
      );
      setStudents(students);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchAllAttendanceData = async () => {
    try {
      const { data, error } = await supabase
        .from('attendance')
        .select('*')
        .eq('level_code', levelCode)
        .order('date', { ascending: false });

      if (error) throw error;

      // Get unique dates where attendance has been taken
      const dates = [...new Set(data.map(record => record.date))].sort((a, b) => new Date(b) - new Date(a));
      setAttendanceDates(dates);

      // Transform data into a more usable format
      const attendanceMap = {};
      data.forEach(record => {
        const key = `${record.student_id}-${record.date}`;
        attendanceMap[key] = record;
      });

      setAttendanceData(attendanceMap);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAttendanceChange = async (studentId, date, status) => {
    const key = `${studentId}-${date}`;
    const existingRecord = attendanceData[key];

    try {
      setIsSubmitting(true);
      setError(null);

      if (existingRecord) {
        // Update existing record
        const { error } = await supabase
          .from('attendance')
          .update({ 
            status,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingRecord.id);

        if (error) throw error;
      } else {
        // Create new record
        const { error } = await supabase
          .from('attendance')
          .insert([{
            student_id: studentId,
            level_code: levelCode,
            date,
            status,
            created_at: new Date().toISOString()
          }]);

        if (error) throw error;
      }

      // Update local state
      setAttendanceData(prev => ({
        ...prev,
        [key]: {
          ...existingRecord,
          student_id: studentId,
          level_code: levelCode,
          date,
          status,
          id: existingRecord?.id || Date.now()
        }
      }));

      // Refresh data to get updated dates
      await fetchAllAttendanceData();
      setSuccess(`Attendance updated for ${students.find(s => s.id === studentId)?.name}`);
      setTimeout(() => setSuccess(null), 3000);
      closeDrawer();

    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAttendanceDay = async (date) => {
    if (!window.confirm(`Delete all attendance for ${formatDate(date)}? This cannot be undone.`)) return;
    try {
      setIsSubmitting(true);
      setError(null);
      const { error } = await supabase
        .from('attendance')
        .delete()
        .eq('level_code', levelCode)
        .eq('date', date);

      if (error) throw error;
      await fetchAllAttendanceData();
      setSuccess(`Attendance for ${formatDate(date)} deleted.`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddNewAttendance = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      // Check if attendance already exists for this date
      const existingAttendance = Object.values(attendanceData).filter(record => record.date === selectedDate);
      
      if (existingAttendance.length > 0) {
        setError(`Attendance already exists for ${formatDate(selectedDate)}. Please edit existing records.`);
        return;
      }

      // Create attendance records for all students
      const recordsToCreate = students.map(student => ({
        student_id: student.id,
        level_code: levelCode,
        date: selectedDate,
        status: 'present', // Default to present
        created_at: new Date().toISOString()
      }));

      const { error } = await supabase
        .from('attendance')
        .insert(recordsToCreate);

      if (error) throw error;

      // Refresh data
      await fetchAllAttendanceData();
      setSuccess(`Attendance added for ${formatDate(selectedDate)}`);
      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'bg-green-100 text-green-700 border-green-300';
      case 'absent': return 'bg-red-100 text-red-700 border-red-300';
      case 'late': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'excused': return 'bg-blue-100 text-blue-700 border-blue-300';
      default: return 'bg-neutral-100 text-neutral-700 border-neutral-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present': return '✅';
      case 'absent': return '❌';
      case 'late': return '⏰';
      case 'excused': return '📝';
      default: return '❓';
    }
  };

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const getAttendanceForStudent = (studentId, date) => {
    const key = `${studentId}-${date}`;
    return attendanceData[key]?.status || '';
  };

  // Calculate attendance statistics for a student
  const getStudentAttendanceStats = (studentId) => {
    const studentAttendance = Object.values(attendanceData).filter(record => record.student_id === studentId);
    
    const stats = {
      present: 0,
      absent: 0,
      late: 0,
      excused: 0,
      total: studentAttendance.length
    };

    studentAttendance.forEach(record => {
      if (record.status) {
        stats[record.status] = (stats[record.status] || 0) + 1;
      }
    });

    // Calculate percentage (present + excused count as full attendance, late counts as 0.5)
    const weightedScore = stats.present + stats.excused + (stats.late * 0.5);
    const totalPossible = stats.total;
    const percentage = totalPossible > 0 ? Math.round((weightedScore / totalPossible) * 100) : 0;

    return { ...stats, percentage };
  };

  // Toggle date expansion
  const toggleDate = (date) => {
    setExpandedDates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else {
        newSet.add(date);
      }
      return newSet;
    });
  };

  // Toggle student expansion
  const toggleStudent = (studentId) => {
    setExpandedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  // Open drawer for editing
  const openDrawer = (type, data) => {
    setDrawerType(type);
    setDrawerData(data);
    setDrawerOpen(true);
  };

  // Close drawer
  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerType(null);
    setDrawerData(null);
  };

  // Render Date View
  const renderDateView = () => {
    if (attendanceDates.length === 0) {
      return (
        <div className="card p-6 text-center">
          <div className="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-neutral-900 mb-1">No Attendance Records</h3>
          <p className="text-sm text-neutral-500 mb-4">No attendance has been taken yet for this class.</p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {attendanceDates.map((date) => {
          const isExpanded = expandedDates.has(date);
          const dateStudents = students.map(student => ({
            ...student,
            status: getAttendanceForStudent(student.id, date)
          }));

          return (
            <div key={date} className="card p-0 overflow-hidden">
              <div className="w-full p-3 flex items-center justify-between hover:bg-neutral-50 transition-colors">
                <button
                  onClick={() => toggleDate(date)}
                  className="flex-1 flex items-center justify-between min-w-0"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-neutral-900 text-sm">{formatDate(date)}</div>
                      <div className="text-xs text-neutral-500">
                        {dateStudents.filter(s => s.status).length} of {students.length} marked
                      </div>
                    </div>
                  </div>
                  <svg 
                    className={`w-5 h-5 text-neutral-400 transition-transform flex-shrink-0 ml-2 ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleDeleteAttendanceDay(date); }}
                  disabled={isSubmitting}
                  className="p-2 rounded-lg text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                  title="Delete this attendance day"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>

              {isExpanded && (
                <div className="border-t border-neutral-200 p-3 space-y-2">
                  {dateStudents.map((student) => {
                    const status = student.status;
                    return (
                      <button
                        key={student.id}
                        onClick={() => openDrawer('date', { student, date, status })}
                        className={`w-full p-2 rounded-lg border text-sm text-left transition-colors ${
                          status 
                            ? getStatusColor(status)
                            : 'bg-neutral-50 text-neutral-500 border-neutral-200 hover:bg-neutral-100'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{student.name}{student.code ? <span className="text-neutral-500"> · {student.code}</span> : ''}</span>
                          {status ? (
                            <span className="flex items-center gap-1">
                              <span>{getStatusIcon(status)}</span>
                              <span className="font-semibold">
                                {status === 'present' ? 'P' : 
                                 status === 'absent' ? 'A' : 
                                 status === 'late' ? 'L' : 
                                 status === 'excused' ? 'E' : '?'}
                              </span>
                            </span>
                          ) : (
                            <span className="text-neutral-400">-</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render Student View
  const renderStudentView = () => {
    if (students.length === 0) {
      return (
        <div className="card p-6 text-center">
          <div className="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-neutral-900 mb-1">No Students</h3>
          <p className="text-sm text-neutral-500">No students enrolled in this class.</p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {students.map((student) => {
          const isExpanded = expandedStudents.has(student.id);
          const stats = getStudentAttendanceStats(student.id);
          const studentDates = attendanceDates.map(date => ({
            date,
            status: getAttendanceForStudent(student.id, date)
          }));

          return (
            <div key={student.id} className="card p-0 overflow-hidden">
              <button
                onClick={() => toggleStudent(student.id)}
                className="w-full p-3 flex items-center justify-between hover:bg-neutral-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-xs">
                      {student.name?.charAt(0)}
                    </span>
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-neutral-900 text-sm">{student.name}{student.code ? <span className="font-normal text-neutral-500"> · {student.code}</span> : ''}</div>
                    <div className="text-xs text-neutral-500">
                      {stats.percentage}% • {stats.present}P {stats.absent}A {stats.late}L {stats.excused}E
                    </div>
                  </div>
                </div>
                <svg 
                  className={`w-5 h-5 text-neutral-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isExpanded && (
                <div className="border-t border-neutral-200 p-3 space-y-2">
                  {studentDates.map(({ date, status }) => (
                    <button
                      key={date}
                      onClick={() => openDrawer('student', { student, date, status })}
                      className={`w-full p-2 rounded-lg border text-sm text-left transition-colors ${
                        status 
                          ? getStatusColor(status)
                          : 'bg-neutral-50 text-neutral-500 border-neutral-200 hover:bg-neutral-100'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{formatDate(date)}</span>
                        {status ? (
                          <span className="flex items-center gap-1">
                            <span>{getStatusIcon(status)}</span>
                            <span className="font-semibold">
                              {status === 'present' ? 'P' : 
                               status === 'absent' ? 'A' : 
                               status === 'late' ? 'L' : 
                               status === 'excused' ? 'E' : '?'}
                            </span>
                          </span>
                        ) : (
                          <span className="text-neutral-400">-</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold text-neutral-900">Attendance</h2>
          <p className="text-sm text-neutral-600">Click to mark attendance</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('date')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'date'
                ? 'bg-primary-600 text-white'
                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
            }`}
          >
            By Date
          </button>
          <button
            onClick={() => setViewMode('student')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'student'
                ? 'bg-primary-600 text-white'
                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
            }`}
          >
            By Student
          </button>
        </div>
      </div>

      {/* Add New Attendance */}
      <div className="card p-3 flex flex-col sm:flex-row items-center gap-3">
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="input text-sm flex-1 w-full sm:w-auto"
        />
        <button
          onClick={handleAddNewAttendance}
          disabled={isSubmitting}
          className="btn-primary text-sm flex items-center justify-center gap-2 py-2 w-full sm:w-auto"
        >
          {isSubmitting ? (
            <>
              <div className="w-3 h-3 loading-spinner"></div>
              Adding...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Attendance for {formatDate(selectedDate)}
            </>
          )}
        </button>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 text-xs text-neutral-600 flex-wrap">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
          P
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-red-500 rounded-full"></span>
          A
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
          L
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
          E
        </span>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-700 font-medium text-sm">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p className="text-green-700 font-medium text-sm">{success}</p>
          </div>
        </div>
      )}

      {/* Content */}
      {viewMode === 'date' ? renderDateView() : renderStudentView()}

      {/* Drawer for Editing */}
      {drawerOpen && drawerData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center sm:justify-center">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[80vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-neutral-200 p-4 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-neutral-900">Mark Attendance</h3>
                <p className="text-sm text-neutral-600">
                  {drawerData.student.name}{drawerData.student.code ? ` · ${drawerData.student.code}` : ''} – {formatDate(drawerData.date)}
                </p>
              </div>
              <button
                onClick={closeDrawer}
                className="p-2 text-neutral-400 hover:text-neutral-600"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Status
                </label>
                <select
                  value={drawerData.status || ''}
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    setDrawerData({ ...drawerData, status: newStatus });
                    if (newStatus) {
                      handleAttendanceChange(drawerData.student.id, drawerData.date, newStatus);
                    }
                  }}
                  className="input w-full"
                  autoFocus
                >
                  <option value="">-</option>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="late">Late</option>
                  <option value="excused">Excused</option>
                </select>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={closeDrawer}
                  className="flex-1 btn-ghost text-sm py-2"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
